import React, { useMemo, useState } from 'react';
import AnalyticsSettingsPanel, { useAnalyticsSettings, SettingsButton } from '../common/AnalyticsSettings';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const AVAILABLE_METRICS = [
  { id: 'totalItems', label: 'Total Items', icon: '📦', defaultVisible: true },
  { id: 'qtyNeeded', label: 'Qty Needed', icon: '📋', defaultVisible: true },
  { id: 'inStock', label: 'In Stock', icon: '✅', defaultVisible: true },
  { id: 'backorder', label: 'Backorder', icon: '⚠️', defaultVisible: true },
];

const AVAILABLE_CHARTS = [
  { id: 'stockStatus', label: 'Stock Status', icon: '🥧', defaultVisible: true, defaultType: 'donut' },
  { id: 'fulfillment', label: 'Fulfillment Overview', icon: '📊', defaultVisible: true, allowTypeChange: false },
  { id: 'byType', label: 'Items by Type', icon: '📊', defaultVisible: true, defaultType: 'bar' },
  { id: 'byPriority', label: 'Items by Priority', icon: '📊', defaultVisible: true, defaultType: 'bar' },
  { id: 'byLocation', label: 'Stock by Location', icon: '📍', defaultVisible: true, defaultType: 'bar' },
  { id: 'byMaterial', label: 'Demand by Material', icon: '🔧', defaultVisible: true, defaultType: 'bar' },
];

/**
 * MaterialAnalyticsDashboard - Analytics Dashboard for Material Log
 * Displays inventory levels, part usage, material types distribution
 */
const MaterialAnalyticsDashboard = ({ data, onClose }) => {
  const [selectedView, setSelectedView] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState } = usePresence('materialAnalytics', 'global', true);
  
  const {
    settings,
    updateSetting,
    toggleMetric,
    toggleChart,
    setChartType,
    resetToDefaults,
    isMetricVisible,
    isChartVisible,
  } = useAnalyticsSettings('material_log', AVAILABLE_METRICS, AVAILABLE_CHARTS);

  // Calculate analytics from data
  const analytics = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalRecords: 0,
        totalQtyNeeded: 0,
        totalInStock: 0,
        totalReceived: 0,
        totalBackorder: 0,
        uniqueTypes: 0,
        uniqueMaterials: 0,
        uniqueLocations: 0,
        byType: [],
        byMaterial: [],
        byPriority: [],
        byLocation: [],
        stockStatus: [],
        receivedTrends: [],
      };
    }

    const totalRecords = data.length;
    const totalQtyNeeded = data.reduce((sum, d) => sum + (parseFloat(d.qty_needed_for_job) || 0), 0);
    const totalInStock = data.reduce((sum, d) => sum + (parseFloat(d.qty_in_stock) || 0), 0);
    const totalReceived = data.reduce((sum, d) => sum + (parseFloat(d.qty_received) || 0), 0);
    const totalBackorder = data.reduce((sum, d) => {
      const needed = parseFloat(d.qty_needed_for_job) || 0;
      const stock = parseFloat(d.qty_in_stock) || 0;
      const received = parseFloat(d.qty_received) || 0;
      return sum + Math.max(0, needed - stock - received);
    }, 0);

    const uniqueTypes = new Set(data.map(d => d.mat_mngmnt_type).filter(Boolean)).size;
    const uniqueMaterials = new Set(data.map(d => d.mat).filter(Boolean)).size;
    const uniqueLocations = new Set(data.map(d => d.location).filter(Boolean)).size;

    // By Type
    const typeMap = {};
    data.forEach(d => {
      const type = d.mat_mngmnt_type || 'Unspecified';
      if (!typeMap[type]) {
        typeMap[type] = { name: type, count: 0, qtyNeeded: 0, inStock: 0, received: 0 };
      }
      typeMap[type].count++;
      typeMap[type].qtyNeeded += parseFloat(d.qty_needed_for_job) || 0;
      typeMap[type].inStock += parseFloat(d.qty_in_stock) || 0;
      typeMap[type].received += parseFloat(d.qty_received) || 0;
    });
    const byType = Object.values(typeMap).sort((a, b) => b.count - a.count);

    // By Material
    const matMap = {};
    data.forEach(d => {
      const mat = d.mat || 'Unspecified';
      if (!matMap[mat]) {
        matMap[mat] = { name: mat, count: 0, qtyNeeded: 0, inStock: 0, received: 0 };
      }
      matMap[mat].count++;
      matMap[mat].qtyNeeded += parseFloat(d.qty_needed_for_job) || 0;
      matMap[mat].inStock += parseFloat(d.qty_in_stock) || 0;
      matMap[mat].received += parseFloat(d.qty_received) || 0;
    });
    const byMaterial = Object.values(matMap).sort((a, b) => b.count - a.count).slice(0, 15);

    // By Priority
    const priorityMap = {};
    data.forEach(d => {
      const priority = d.priority || 'Unassigned';
      if (!priorityMap[priority]) {
        priorityMap[priority] = { name: priority, count: 0, qtyNeeded: 0 };
      }
      priorityMap[priority].count++;
      priorityMap[priority].qtyNeeded += parseFloat(d.qty_needed_for_job) || 0;
    });
    const byPriority = Object.values(priorityMap).sort((a, b) => {
      const order = { 'High': 0, 'Medium': 1, 'Low': 2, 'Unassigned': 3 };
      return (order[a.name] ?? 4) - (order[b.name] ?? 4);
    });

    // By Location
    const locationMap = {};
    data.forEach(d => {
      const location = d.location || 'Unspecified';
      if (!locationMap[location]) {
        locationMap[location] = { name: location, count: 0, inStock: 0 };
      }
      locationMap[location].count++;
      locationMap[location].inStock += parseFloat(d.qty_in_stock) || 0;
    });
    const byLocation = Object.values(locationMap).sort((a, b) => b.count - a.count).slice(0, 10);

    // Stock Status
    let fullyStocked = 0, partiallyStocked = 0, outOfStock = 0, overStocked = 0;
    data.forEach(d => {
      const needed = parseFloat(d.qty_needed_for_job) || 0;
      const available = (parseFloat(d.qty_in_stock) || 0) + (parseFloat(d.qty_received) || 0);
      if (needed === 0 && available > 0) overStocked++;
      else if (available >= needed && needed > 0) fullyStocked++;
      else if (available > 0 && available < needed) partiallyStocked++;
      else if (available === 0 && needed > 0) outOfStock++;
    });
    const stockStatus = [
      { name: 'Fully Stocked', count: fullyStocked, color: '#10b981' },
      { name: 'Partially Stocked', count: partiallyStocked, color: '#f59e0b' },
      { name: 'Out of Stock', count: outOfStock, color: '#ef4444' },
      { name: 'Over Stocked', count: overStocked, color: '#3b82f6' },
    ];

    // Received trends by date
    const dateMap = {};
    data.forEach(d => {
      const date = d.received_date?.split('T')[0];
      if (date) {
        if (!dateMap[date]) {
          dateMap[date] = { date, received: 0, count: 0 };
        }
        dateMap[date].received += parseFloat(d.qty_received) || 0;
        dateMap[date].count++;
      }
    });
    const receivedTrends = Object.values(dateMap).sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));

    return {
      totalRecords,
      totalQtyNeeded,
      totalInStock,
      totalReceived,
      totalBackorder,
      uniqueTypes,
      uniqueMaterials,
      uniqueLocations,
      byType,
      byMaterial,
      byPriority,
      byLocation,
      stockStatus,
      receivedTrends,
      fulfillmentRate: totalQtyNeeded > 0 ? ((totalInStock + totalReceived) / totalQtyNeeded * 100) : 0,
    };
  }, [data]);

  const colors = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    dark: '#1e293b',
    chartColors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'],
  };

  const formatNumber = (num, decimals = 0) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toFixed(decimals);
  };

  // Metric Card
  const MetricCard = ({ title, value, subtitle, icon, color }) => (
    <div style={{
      backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      </div>
      <div style={{ fontSize: '1.75rem', fontWeight: '700', color: colors.dark, margin: '0.5rem 0' }}>{value}</div>
      {subtitle && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</span>}
    </div>
  );

  // Bar Chart
  const BarChart = ({ data, valueKey, labelKey, title, maxBars = 10 }) => {
    const chartData = data.slice(0, maxBars);
    const maxValue = Math.max(...chartData.map(d => d[valueKey] || 0), 1);

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {chartData.map((item, idx) => {
            const value = item[valueKey] || 0;
            const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '100px', fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item[labelKey]}>
                  {item[labelKey]}
                </div>
                <div style={{ flex: 1, height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${percentage}%`, height: '100%',
                    backgroundColor: colors.chartColors[idx % colors.chartColors.length],
                    borderRadius: '4px', transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ width: '60px', fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>
                  {formatNumber(value)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Donut Chart
  const DonutChart = ({ data, valueKey, labelKey, title }) => {
    const total = data.reduce((sum, d) => sum + (d[valueKey] || 0), 0);
    let currentAngle = 0;
    
    const segments = data.map((item, idx) => {
      const value = item[valueKey] || 0;
      const percentage = total > 0 ? (value / total) * 100 : 0;
      const angle = (percentage / 100) * 360;
      const startAngle = currentAngle;
      currentAngle += angle;
      return { ...item, percentage, angle, startAngle, color: item.color || colors.chartColors[idx % colors.chartColors.length] };
    });

    let gradient = 'conic-gradient(';
    segments.forEach((seg, idx) => {
      gradient += `${seg.color} ${seg.startAngle}deg ${seg.startAngle + seg.angle}deg`;
      if (idx < segments.length - 1) gradient += ', ';
    });
    gradient += ')';

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{ position: 'relative', width: '120px', height: '120px' }}>
            <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: total > 0 ? gradient : '#e2e8f0' }} />
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: '70px', height: '70px', borderRadius: '50%', backgroundColor: 'white',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.dark }}>{formatNumber(total)}</div>
              <div style={{ fontSize: '0.6rem', color: '#64748b' }}>items</div>
            </div>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {segments.map((seg, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: seg.color }} />
                <span style={{ flex: 1, color: '#64748b' }}>{seg[labelKey]}</span>
                <span style={{ fontWeight: '500', color: colors.dark }}>{seg.percentage.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Progress Bar
  const ProgressBar = ({ value, max, label, color }) => {
    const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{label}</span>
          <span style={{ fontSize: '0.85rem', fontWeight: '600', color: colors.dark }}>{percentage.toFixed(1)}%</span>
        </div>
        <div style={{ height: '10px', backgroundColor: '#e2e8f0', borderRadius: '5px', overflow: 'hidden' }}>
          <div style={{ width: `${percentage}%`, height: '100%', backgroundColor: color, transition: 'width 0.5s ease' }} />
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'inventory', label: 'Inventory', icon: '📦' },
    { id: 'breakdown', label: 'Breakdown', icon: '📋' },
  ];

  const renderOverview = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {isMetricVisible('totalItems') && <MetricCard title="Total Items" value={formatNumber(analytics.totalRecords)} subtitle={`${analytics.uniqueTypes} types`} icon="📦" color={colors.primary} />}
        {isMetricVisible('qtyNeeded') && <MetricCard title="Qty Needed" value={formatNumber(analytics.totalQtyNeeded)} subtitle="Total required" icon="📋" color={colors.info} />}
        {isMetricVisible('inStock') && <MetricCard title="In Stock" value={formatNumber(analytics.totalInStock)} subtitle={`${analytics.uniqueLocations} locations`} icon="✅" color={colors.success} />}
        {isMetricVisible('backorder') && <MetricCard title="Backorder" value={formatNumber(analytics.totalBackorder)} subtitle="Shortage" icon="⚠️" color={colors.danger} />}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {isChartVisible('stockStatus') && <DonutChart data={analytics.stockStatus} valueKey="count" labelKey="name" title="Stock Status Distribution" />}
        {isChartVisible('fulfillment') && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>Fulfillment Overview</h4>
            <ProgressBar value={analytics.totalInStock + analytics.totalReceived} max={analytics.totalQtyNeeded} label="Overall Fulfillment" color={colors.success} />
            <ProgressBar value={analytics.totalInStock} max={analytics.totalQtyNeeded} label="In Stock" color={colors.info} />
            <ProgressBar value={analytics.totalReceived} max={analytics.totalQtyNeeded} label="Received" color={colors.primary} />
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        {isChartVisible('byType') && <BarChart data={analytics.byType} valueKey="count" labelKey="name" title="Items by Type" />}
        {isChartVisible('byPriority') && <BarChart data={analytics.byPriority} valueKey="count" labelKey="name" title="Items by Priority" />}
      </div>
    </>
  );

  const renderInventory = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard title="Fulfillment Rate" value={`${analytics.fulfillmentRate.toFixed(1)}%`} icon="📈" color={analytics.fulfillmentRate >= 80 ? colors.success : analytics.fulfillmentRate >= 50 ? colors.warning : colors.danger} />
        <MetricCard title="Unique Materials" value={analytics.uniqueMaterials} icon="🔧" color={colors.info} />
        <MetricCard title="Total Received" value={formatNumber(analytics.totalReceived)} icon="📥" color={colors.primary} />
        <MetricCard title="Locations" value={analytics.uniqueLocations} icon="📍" color={colors.secondary} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        {isChartVisible('byLocation') && <BarChart data={analytics.byLocation} valueKey="inStock" labelKey="name" title="Stock by Location" />}
        {isChartVisible('byMaterial') && <BarChart data={analytics.byMaterial} valueKey="qtyNeeded" labelKey="name" title="Demand by Material" maxBars={12} />}
      </div>
    </>
  );

  const renderBreakdown = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <BarChart data={analytics.byType} valueKey="qtyNeeded" labelKey="name" title="Qty Needed by Type" />
        <BarChart data={analytics.byType} valueKey="inStock" labelKey="name" title="In Stock by Type" />
      </div>
      {analytics.receivedTrends.length > 0 && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
          <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>Receiving Trends</h4>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '150px' }}>
            {analytics.receivedTrends.slice(-30).map((d, idx) => {
              const maxReceived = Math.max(...analytics.receivedTrends.map(t => t.received), 1);
              const height = (d.received / maxReceived) * 100;
              return (
                <div key={idx} style={{ flex: 1, backgroundColor: colors.primary, borderRadius: '2px 2px 0 0', height: `${height}%`, minHeight: '2px' }} title={`${d.date}: ${d.received} received`} />
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.7rem', color: '#94a3b8' }}>
            <span>{analytics.receivedTrends[Math.max(0, analytics.receivedTrends.length - 30)]?.date || ''}</span>
            <span>{analytics.receivedTrends[analytics.receivedTrends.length - 1]?.date || ''}</span>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
      zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem',
    }}>
      <div style={{
        backgroundColor: '#f1f5f9', borderRadius: '16px', width: '100%', maxWidth: '1400px', maxHeight: '95vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      }}>
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem', background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>📦 Material Analytics</h2>
            <p style={{ margin: '0.25rem 0 0', opacity: 0.9, fontSize: '0.85rem' }}>Inventory levels, stock status, and material distribution</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            <SettingsButton onClick={() => setShowSettings(true)} />
            <button onClick={onClose} style={{
              padding: '0.5rem 1rem', backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem',
            }}>✕ Close</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setSelectedView(tab.id)} style={{
              padding: '0.75rem 1.25rem', backgroundColor: selectedView === tab.id ? colors.primary : 'transparent',
              color: selectedView === tab.id ? 'white' : '#64748b', border: 'none', borderRadius: '8px 8px 0 0',
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {analytics.totalRecords === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
              <h3 style={{ margin: 0, color: colors.dark }}>No Data Available</h3>
              <p style={{ margin: '0.5rem 0 0' }}>Add materials to see analytics</p>
            </div>
          ) : (
            <>
              {selectedView === 'overview' && renderOverview()}
              {selectedView === 'inventory' && renderInventory()}
              {selectedView === 'breakdown' && renderBreakdown()}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '0.75rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#94a3b8',
        }}>
          <span>{analytics.totalRecords} total items</span>
          <span>{analytics.uniqueTypes} types | {analytics.uniqueMaterials} materials | {analytics.uniqueLocations} locations</span>
        </div>
      </div>
      
      {/* Settings Panel */}
      <AnalyticsSettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        updateSetting={updateSetting}
        toggleMetric={toggleMetric}
        toggleChart={toggleChart}
        setChartType={setChartType}
        resetToDefaults={resetToDefaults}
        availableMetrics={AVAILABLE_METRICS}
        availableCharts={AVAILABLE_CHARTS}
      />
    </div>
  );
};

export default MaterialAnalyticsDashboard;

